import styled from '@emotion/native';
import type { TaskCreate } from '@quromedical/models';
import { Row } from 'components/base';
import { Button, type IconName, Visible } from 'design-system/base';
import { Section } from 'design-system/layout';
import { BASE_SIZE, MARGIN } from 'design-system/theme';
import { usePatient } from 'hooks/usePatient';
import { useIsBiggerThan } from 'hooks/useResponsive';
import type React from 'react';

import { PatientBreadcrumb } from './PatientBreadcrumb';
import { PatientVitalsSummaryCard } from './PatientVitalsSummaryCard';

interface ActionProps {
  icon: IconName;
  onPress: () => void;
}

interface PatientHeaderSectionProps {
  id: string;
  action?: ActionProps | React.FC<CustomAction>;
  create?: (body: TaskCreate) => Promise<void>;
}

const ReducedSection = styled(Section)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  // setting a height so that the bar size is consistent with or without a button
  height: BASE_SIZE[32],
  // individually set so as to override the `marginVertical` from the section
  marginTop: MARGIN['2xs'],
  marginBottom: MARGIN.xs,
});

export interface CustomAction {
  id: string;
  create: (body: TaskCreate) => Promise<void>;
}

export const PatientHeaderSection: React.FC<PatientHeaderSectionProps> = ({
  action: Action,
  id,
  create,
}) => {
  const isBig = useIsBiggerThan('lg');

  const { general } = usePatient(id);
  const isActionConfig = typeof Action === 'object';
  const isActionComponent = typeof Action === 'function';

  return (
    <>
      <Visible if={isBig}>
        <ReducedSection>
          <PatientBreadcrumb id={id} patient={general} />
          {isActionConfig ? (
            <Row>
              <Button
                onPress={Action.onPress}
                icon={Action.icon}
                variant="flat"
              />
            </Row>
          ) : null}

          {isActionComponent && create ? (
            <Row>
              <Action id={id} create={create} />
            </Row>
          ) : null}
        </ReducedSection>
      </Visible>

      <Section hasTopMargin={false}>
        <PatientVitalsSummaryCard id={id} />
      </Section>
    </>
  );
};
